'use client';

import type { DataManagementContextType } from '../../interfaces/DataManagementContextType';
import { DataManagementProvider } from './data-management/DataManagementProvider';
import { DataManagementHeader } from './data-management/DataManagementHeader';
import { DataManagementTable } from './data-management/DataManagementTable';
import { DataManagementDialog } from './data-management/DataManagementDialog';
import { DataManagementForm } from './data-management/DataManagementForm';
import type { DataManagementViewProps } from '../../interfaces/DataManagementViewProps';
import { useAuthorize } from '@shared/auth/src/hooks';
import type { FieldValues } from 'react-hook-form';

export function DataManagementView<TData extends FieldValues>({
  data,
  isLoading,
  selectedItem,
  isOpen,
  onOpenChange,
  onSubmit,
  handleDelete,
  onSelectItem,
  title,
  mappings,
  schema,
  isDeleting,
  actions,
  resource
}: DataManagementViewProps<TData>) {
  const { hasPermission: canCreate } = useAuthorize(resource, ['create']);
  const { hasPermission: canRead } = useAuthorize(resource, ['read']);
  const { hasPermission: canUpdate } = useAuthorize(resource, ['update']);
  const { hasPermission: canDelete } = useAuthorize(resource, ['delete']);
  const { hasPermission: canManage } = useAuthorize(resource, ['manage']);
  const { hasPermission: canApprove } = useAuthorize(resource, ['approve']);
  const { hasPermission: canReject } = useAuthorize(resource, ['reject']);
  const { hasPermission: canExport } = useAuthorize(resource, ['export']);
  const { hasPermission: canImport } = useAuthorize(resource, ['import']);

  // If user doesn't have read permission, don't render anything
  if (!canRead) {
    return null;
  }

  const permissions = {
    verbs: {
      create: canCreate,
      read: canRead,
      update: canUpdate,
      delete: canDelete,
      manage: canManage,
      approve: canApprove,
      reject: canReject,
      export: canExport,
      import: canImport
    }
  };

  const contextValue: DataManagementContextType<TData> = {
    data,
    isLoading,
    selectedItem,
    isOpen,
    onOpenChange,
    onSubmit,
    handleDelete,
    onSelectItem,
    title,
    mappings,
    permissions,
    schema,
    isDeleting
  };

  return (
    <DataManagementProvider<TData> {...contextValue}>
      <div className="space-y-4">
        <DataManagementHeader />
        <DataManagementTable actions={actions} />
        <DataManagementDialog>
          <DataManagementForm<TData> />
        </DataManagementDialog>
      </div>
    </DataManagementProvider>
  );
} 